// ============================================================
//  src/admin/pages/AdminUserDetailPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getUser, userAction, adminListPlans } from '../api/adminClient';
import { Badge, isFlagTrue } from '../components/UI';
import DataTable from '../components/DataTable';
import { useConfirm } from '../components/ConfirmModal';

const STATUS_LABEL = { 0: 'Unverified', 1: 'Active', 2: 'Suspended' };
const STATUS_TONE  = { 0: 'warning', 1: 'success', 2: 'danger' };

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtNaira(n) { return '₦' + (Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 }); }

export default function AdminUserDetailPage() {
  const { sId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [extendDate, setExtendDate] = useState('');
  const [walletDir, setWalletDir] = useState('credit');
  const [walletAmt, setWalletAmt] = useState('');
  const [walletNote, setWalletNote] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getUser(sId), adminListPlans()])
      .then(([u, p]) => {
        setData(u.data);
        setPlans(p.data.plans.filter(pl => isFlagTrue(pl.plIsActive)));
        setSelectedPlan(u.data.subscription?.plSlug || '');
      })
      .catch(() => toast.error('Could not load user'))
      .finally(() => setLoading(false));
  }, [sId]);

  useEffect(() => { load(); }, [load]);

  async function doAction(action, extra = {}) {
    setBusy(true);
    try {
      const res = await userAction({ action, sId, ...extra });
      toast.success(res.data.message || 'Done');
      if (action === 'reset_password' && res.data.new_password) {
        toast(`Temporary password: ${res.data.new_password}`, { duration: 10000, icon: '🔑' });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleBlock() {
    const ok = await confirm({
      title: 'Block this user?',
      message: 'They will be logged out immediately and unable to sign in until unblocked.',
      confirmText: 'Block User',
      tone: 'danger',
    });
    if (ok) doAction('block');
  }

  async function handleResetPassword() {
    const ok = await confirm({
      title: 'Reset password?',
      message: "This generates a new temporary password for the user. Their current password will stop working right away.",
      confirmText: 'Reset Password',
      tone: 'danger',
    });
    if (ok) doAction('reset_password');
  }

  async function handleWalletAdjust() {
    const amt = Math.round(Number(walletAmt) * 100) / 100;
    if (!(amt > 0)) { toast.error('Enter an amount'); return; }
    if (walletNote.trim().length < 3) { toast.error('Add a reason — the user sees it in their history'); return; }
    const debit = walletDir === 'debit';
    const ok = await confirm({
      title: debit ? `Debit ${fmtNaira(amt)}?` : `Credit ${fmtNaira(amt)}?`,
      message: `${debit ? 'Removes' : 'Adds'} ${fmtNaira(amt)} ${debit ? 'from' : 'to'} this user's wallet. Reason shown to them: “${walletNote.trim()}”`,
      confirmText: debit ? 'Debit wallet' : 'Credit wallet',
      tone: debit ? 'danger' : undefined,
    });
    if (!ok) return;
    await doAction('wallet_adjust', { amount: debit ? -amt : amt, note: walletNote.trim() });
    setWalletAmt(''); setWalletNote('');
  }

  if (loading) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading user…</div>;
  if (!data) return <div className="bf-empty">User not found.</div>;

  const { user, bots, subscription, payments, access, wallet } = data;
  const accTone = !access ? 'muted' : access.state === 'expired' ? 'danger' : access.state === 'trial' ? 'warning' : 'success';
  const accLabel = !access ? '—' : access.state === 'expired' ? 'Bots paused' : access.state === 'trial' ? 'On free trial' : 'Active';

  const botColumns = [
    { key: 'name', label: 'Name', primary: true, render: b => b.bName },
    { key: 'users', label: 'Users', render: b => b.totalUsers },
    {
      key: 'status', label: 'Status',
      render: b => isFlagTrue(b.bAdminSuspended)
        ? <Badge tone="danger">Suspended</Badge>
        : isFlagTrue(b.bIsActive) ? <Badge tone="success">Active</Badge> : <Badge tone="muted">Inactive</Badge>,
    },
    { key: 'created', label: 'Created', render: b => fmtDate(b.bCreatedAt) },
  ];

  const paymentColumns = [
    { key: 'plan', label: 'Plan', primary: true, render: p => p.plName },
    { key: 'amount', label: 'Amount', render: p => fmtNaira(p.payAmount) },
    { key: 'status', label: 'Status', render: p => p.payStatus },
    { key: 'date', label: 'Date', render: p => fmtDate(p.payCreatedAt) },
  ];

  return (
    <div className="bf-page">
      <Link to="/bot-father/users" className="bf-back-link">← Back to Users</Link>
      <div className="bf-detail-header">
        <div>
          <div className="bf-detail-name">{user.sFname} {user.sLname}</div>
          <div className="bf-detail-meta">{user.sEmail} · {user.sPhone || 'No phone'}</div>
        </div>
        <Badge tone={STATUS_TONE[user.sStatus]}>{STATUS_LABEL[user.sStatus]}</Badge>
      </div>

      {/* Actions */}
      <div className="bf-card">
        <div className="bf-card-hdr">Actions</div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="bf-btn-row">
            {Number(user.sStatus) === 2 ? (
              <button className="btn btn-primary" disabled={busy} onClick={() => doAction('unblock')}>Unblock User</button>
            ) : (
              <button className="bf-btn-danger-outline" disabled={busy} onClick={handleBlock}>Block User</button>
            )}

            {Number(user.sStatus) === 0 && (
              <button className="btn btn-outline" disabled={busy} onClick={() => doAction('verify_email')}>Verify Email</button>
            )}

            <button className="btn btn-outline" disabled={busy} onClick={handleResetPassword}>
              Reset Password
            </button>
          </div>

          {/* Change plan */}
          <div className="bf-flex-wrap" style={{ alignItems: 'center' }}>
            <label style={{ fontSize: 13, fontWeight: 700 }}>Plan:</label>
            <select className="bf-select" value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
              {plans.map(p => <option key={p.plSlug} value={p.plSlug}>{p.plName} ({fmtNaira(p.plPrice)})</option>)}
            </select>
            <button className="btn btn-primary" disabled={busy || selectedPlan === subscription?.plSlug}
                    onClick={() => doAction('change_plan', { plan_slug: selectedPlan })}>
              Update Plan
            </button>
          </div>
        </div>
      </div>

      {/* Plan access — what actually decides whether this user's bots reply */}
      <div className="bf-card">
        <div className="bf-card-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span>Plan &amp; access</span>
          {access && <Badge tone={accTone}>{accLabel}</Badge>}
        </div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13.5 }}>
          {access ? (
            <div>
              <div><strong>{access.planName}</strong>{Number(access.planPrice) > 0 ? ` — ${fmtNaira(access.planPrice)}` : ` — ${access.trialDays}-day trial (once per account)`}</div>
              <div className="bf-muted" style={{ marginTop: 4 }}>
                {access.expiresAt
                  ? `${access.state === 'expired' ? 'Ended' : 'Ends'} ${fmtDate(access.expiresAt)}${access.state !== 'expired' && access.daysLeft != null ? ` · ${access.daysLeft} day${access.daysLeft === 1 ? '' : 's'} left` : ''}`
                  : 'No end date'}
                {subscription?.subStartedAt && ` · Started ${fmtDate(subscription.subStartedAt)}`}
                {access.source === 'signup' && ' · Automatic trial from sign-up'}
              </div>
              {Number(access.rentalBots) > 0 && (
                <div className="bf-muted" style={{ marginTop: 6 }}>
                  📱 {access.rentalBots} bot{Number(access.rentalBots) === 1 ? '' : 's'} on Rent Space numbers — covered by the rental, not by this plan.
                </div>
              )}
            </div>
          ) : subscription ? (
            <div><strong>{subscription.plName}</strong> — {fmtNaira(subscription.plPrice)}</div>
          ) : <div className="bf-muted">No active subscription.</div>}

          <div style={{ borderTop: '1px solid var(--bf-border, #e5e7eb)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--bf-text-muted, #6b7280)' }}>Extend this plan</div>
            <div className="bf-btn-row">
              {[7, 30, 90].map(d => (
                <button key={d} className="btn btn-outline" disabled={busy} onClick={() => doAction('extend_plan', { add_days: d })}>+{d} days</button>
              ))}
            </div>
            <div className="bf-flex-wrap" style={{ alignItems: 'center' }}>
              <label style={{ fontSize: 13, fontWeight: 700 }}>Or end on:</label>
              <input type="date" className="bf-input" style={{ maxWidth: 190 }} value={extendDate} onChange={e => setExtendDate(e.target.value)} />
              <button className="btn btn-primary" disabled={busy || !extendDate}
                      onClick={async () => { await doAction('extend_plan', { expires_at: extendDate }); setExtendDate(''); }}>
                Set end date
              </button>
              {access && !access.isTrial && (
                <button className="btn btn-outline" disabled={busy} onClick={() => doAction('extend_plan', { expires_at: 'never' })}>No expiry</button>
              )}
            </div>
            <div className="bf-muted" style={{ fontSize: 12 }}>
              Days are added to the current end date, or to today if it has already ended. Bots start replying again straight away.
            </div>
          </div>
        </div>
      </div>

      {/* Wallet — prepaid balance, auto-renew, manual credit/debit */}
      {wallet && (
        <div className="bf-card">
          <div className="bf-card-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span>Wallet</span>
            <Badge tone={wallet.autoRenewPlan ? 'success' : 'muted'}>{wallet.autoRenewPlan ? 'Plan auto-renew on' : 'Auto-renew off'}</Badge>
          </div>
          <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13.5 }}>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em' }}>{fmtNaira(wallet.balance)}</div>

            <div style={{ borderTop: '1px solid var(--bf-border, #e5e7eb)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--bf-text-muted, #6b7280)' }}>Adjust balance</div>
              <div className="bf-flex-wrap" style={{ alignItems: 'center' }}>
                <select className="bf-select" value={walletDir} onChange={e => setWalletDir(e.target.value)} style={{ maxWidth: 120 }}>
                  <option value="credit">Credit +</option>
                  <option value="debit">Debit −</option>
                </select>
                <input type="number" min="1" step="1" inputMode="decimal" className="bf-input" style={{ maxWidth: 150 }} placeholder="Amount ₦"
                       value={walletAmt} onChange={e => setWalletAmt(e.target.value)} />
                <input type="text" maxLength={200} className="bf-input" style={{ flex: '1 1 220px' }} placeholder="Reason (user sees this) e.g. Refund for outage"
                       value={walletNote} onChange={e => setWalletNote(e.target.value)} />
                <button className={walletDir === 'debit' ? 'bf-btn-danger-outline' : 'btn btn-primary'} disabled={busy || !walletAmt}
                        onClick={handleWalletAdjust}>
                  {walletDir === 'debit' ? 'Debit' : 'Credit'}
                </button>
              </div>
              <div className="bf-muted" style={{ fontSize: 12 }}>Every change is recorded in the user's wallet history and the admin log. Debits can't take the balance below ₦0.</div>
            </div>

            {wallet.ledger?.length > 0 && (
              <div style={{ borderTop: '1px solid var(--bf-border, #e5e7eb)', paddingTop: 10 }}>
                {wallet.ledger.map(l => {
                  const credit = l.lType === 'credit';
                  return (
                    <div key={l.lId} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px dashed var(--bf-border, #e5e7eb)' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.lNote || l.lKind}</div>
                        <div className="bf-muted" style={{ fontSize: 12 }}>{fmtDate(l.lCreatedAt)} · {l.lKind} · bal {fmtNaira(l.lBalanceAfter)}</div>
                      </div>
                      <div style={{ fontWeight: 700, whiteSpace: 'nowrap', color: credit ? '#00845D' : '#B42318' }}>
                        {credit ? '+' : '−'}{fmtNaira(l.lAmount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bots */}
      <div className="bf-card">
        <div className="bf-card-hdr">Bots ({bots.length})</div>
        <div className="bf-card-body-flush">
          <DataTable
            columns={botColumns}
            rows={bots}
            keyField="bId"
            onRowClick={b => navigate(`/bot-father/bots/${b.bId}`)}
            emptyMessage="No bots created yet."
          />
        </div>
      </div>

      {/* Payment history */}
      <div className="bf-card">
        <div className="bf-card-hdr">Payment History</div>
        <div className="bf-card-body-flush">
          <DataTable
            columns={paymentColumns}
            rows={payments}
            keyField="payId"
            emptyMessage="No payments yet."
          />
        </div>
      </div>
    </div>
  );
}